import { type TFunction } from 'i18next';
import { getIsValid } from './utils';
import type {
  MessageKey,
  ValidationFunctionResult,
  ValidationResult,
  ValidationResultBasic,
  ValidationRule,
} from './types';

export function check<T>(
  t: TFunction<'formValidation'>,
  value: T | null | undefined,
  rules: ValidationRule<T>[],
): Required<ValidationResult> {
  let isValid = true;
  const errorMessages = [];
  const errorMessageParams = [];
  const localResultsPerItem = initResultsPerItem<T>(value);

  for (const rule of rules) {
    const { fn, messageKey, messageParams } = rule;
    const result = fn(value);
    if (getIsValid(result)) {
      continue;
    }

    isValid = false;
    if (typeof result === 'boolean' || !('results' in result)) {
      const messageParams = extractMessageParams(result, rule);
      errorMessages.push(getMessage(t, messageKey, messageParams));
      errorMessageParams.push(messageParams);
      continue;
    }

    for (const [i, oneResult] of result.results.entries()) {
      if (!oneResult.isValid) {
        localResultsPerItem[i]!.isValid = false;
        localResultsPerItem[i]!.errorMessages.push(
          getMessage(t, messageKey, {
            ...messageParams,
            ...oneResult.params,
          }),
        );
      }
    }
  }

  return {
    isValid: isValid,
    errorMessages,
    errorMessageParams,
    resultsPerItem: localResultsPerItem,
  };
}

function initResultsPerItem<T>(
  value: T | null | undefined,
): ValidationResultBasic[] {
  return Array.isArray(value)
    ? new Array<ValidationResultBasic>(value.length)
        .fill({ isValid: true, errorMessages: [] })
        .map(() => ({ isValid: true, errorMessages: [] }))
    : [];
}

function extractMessageParams<T>(
  result: ValidationFunctionResult<T, object>,
  rule: ValidationRule<T>,
): object | undefined {
  if (typeof result === 'boolean') {
    return rule.messageParams;
  } else if (!('results' in result)) {
    return { ...rule.messageParams, ...result.params };
  }
  return undefined;
}

export function getMessage(
  t: TFunction<'formValidation'>,
  messageKey: MessageKey,
  params?: object,
): string {
  // Helper function to translate all values inside the params object
  const translateValues = (obj: object): object => {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        typeof value === 'string' ? t(value as MessageKey) : value, // Translate if it's a string
      ]),
    );
  };
  // @ts-expect-error typescript does not like the messageParams object type. But I don't know why
  return t(messageKey, params ? translateValues(params) : undefined);
}
